import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, faExclamationTriangle, faCheckCircle,
  faArrowLeft, faArrowRight, faPaperPlane, faShieldAlt,
  faHome, faEye, faChartLine, faSave, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  loadQuestionnaire, 
  saveAnswer, 
  submitQuestionnaire,
  loadPortalQuestionnaire,
  savePortalAnswer,
  submitPortalQuestionnaire,
  SupplierQuestionnaireData,
  Question,
  Answer
} from '../../services/supplierApi';

import QuestionRenderer from '../../components/supplier/QuestionRenderer';
import SubmissionModal from '../../components/supplier/SubmissionModal';
import wedlLogo from '../../assets/images/wedl_logo.svg';
import { useLanguage } from '../../contexts/LanguageContext';

const SupplierQuestionnaire: React.FC = () => {
  const { accessToken, masterToken } = useParams<{ accessToken: string; masterToken?: string }>();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const { language: contextLanguage } = useLanguage(); // Get language from context
  
  // **PORTAL-PARAMETER EXTRAHIEREN**
  const isPortalMode = !!masterToken;
  const portalSessionToken = searchParams.get('session');
  const supplierName = searchParams.get('supplier');
  const supplierEmail = searchParams.get('email');
  const isReadonly = searchParams.get('readonly') === 'true';
  const urlLanguage = searchParams.get('lang') as 'de' | 'en' | null;
  
  // **STATE**
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionnaireData, setQuestionnaireData] = useState<SupplierQuestionnaireData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, Answer>>(new Map());
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  // **NEW: Artikel-Auswahl State**
  const [articleSelections, setArticleSelections] = useState<Map<string, string[]>>(new Map());

  // **LANGUAGE FROM URL, CONTEXT, OR SUBMISSION - CONTEXT HAS PRIORITY**
  const language: 'de' | 'en' = (urlLanguage || contextLanguage || questionnaireData?.submission?.language || 'de') as 'de' | 'en';

  // **TRANSLATIONS**
  const translations = {
    de: {
      back: 'Zurück',
      next: 'Weiter',
      submit: 'Abschließen',
      viewOnly: 'Nur Ansicht',
      question: 'Frage',
      of: 'von',
      answered: 'beantwortet',
      completed: 'abgeschlossen',
      thankYou: 'Vielen Dank!',
      questionnaireCompleted: 'Ihr Fragebogen wurde erfolgreich abgeschlossen. Wir haben Ihre Antworten erhalten.',
      supplier: 'Lieferant',
      questionnaire: 'Fragebogen',
      completedOn: 'Abgeschlossen',
      portal: 'Portal',
      loadingQuestionnaire: 'Fragebogen wird geladen',
      loadingMessage: 'Berechtigung wird überprüft und Daten werden geladen...',
      errorLoading: 'Fehler beim Laden',
      errorMessage: 'Fragebogen konnte nicht geladen werden',
      backToPortal: 'Zurück zum Portal',
      tryAgain: 'Nochmal versuchen',
      toLogin: 'Zur Anmeldung',
      reloadPage: 'Seite neu laden'
    },
    en: {
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      viewOnly: 'View only',
      question: 'Question',
      of: 'of',
      answered: 'answered',
      completed: 'completed',
      thankYou: 'Thank you!',
      questionnaireCompleted: 'Your questionnaire has been successfully completed. We have received your answers.',
      supplier: 'Supplier',
      questionnaire: 'Questionnaire',
      completedOn: 'Completed on',
      portal: 'Portal',
      loadingQuestionnaire: 'Loading questionnaire',
      loadingMessage: 'Checking permissions and loading data...',
      errorLoading: 'Error loading',
      errorMessage: 'Failed to load questionnaire',
      backToPortal: 'Back to portal',
      tryAgain: 'Try again',
      toLogin: 'Go to login',
      reloadPage: 'Reload page'
    }
  };

  const t = translations[language as 'de' | 'en'] || translations.de;

  // **HELPER FUNCTIONS**
  const isUsingPortalSession = () => {
    const fromSupplierLogin = sessionStorage.getItem('fromSupplierLogin');
    return fromSupplierLogin === 'true' || (isPortalMode && masterToken);
  };

  const getPortalMasterToken = () => {
    return masterToken || sessionStorage.getItem('supplierPortalMasterToken');
  };

  const shouldUsePortalAPI = () => {
    const fromSupplierLogin = sessionStorage.getItem('fromSupplierLogin');
    const portalMasterToken = sessionStorage.getItem('supplierPortalMasterToken');
    return (fromSupplierLogin === 'true' && portalMasterToken) || (isPortalMode && masterToken);
  };

  const getTemplateTitle = () => {
    const title = questionnaireData?.template?.title;
    if (!title) return t.questionnaire;
    if (typeof title === 'string') return title;
    // Use current language to get correct title
    return title[language] || title.de || title.en || t.questionnaire;
  };

  const visibleQuestions = useCallback(() => {
    if (!questionnaireData?.template?.questions) return [];
    // Filter out sections, but keep info_text and admin_file as they provide content
    return questionnaireData.template.questions.filter(q => 
      q && q.id && q.type !== 'section'
    ) || [];
  }, [questionnaireData?.template?.questions]);

  // **NEW: Get only navigable questions**
  // Eine Section sammelt alle nachfolgenden Fragen bis zur nächsten Section
  const navigableQuestions = useCallback(() => {
    if (!questionnaireData?.template?.questions) return [];
    
    const allQs = questionnaireData.template.questions.filter(q => q && q.id);
    
    const navigable: any[] = [];
    let insideSection = false;
    
    for (let i = 0; i < allQs.length; i++) {
      const question = allQs[i];
      
      if (question.type === 'section') {
        // Section startet eine neue navigierbare Frage
        navigable.push(question);
        insideSection = true;
        continue;
      }
      
      if (insideSection) {
        // Wir sind innerhalb einer Section - sammle bis zur nächsten Section
        // Alle Fragen gehören zur Section und sind nicht navigierbar
        continue;
      }
      
      // Normale Fragen außerhalb von Sections
      if (question.type === 'admin_file' || question.type === 'info_text') {
        // info_text/admin_file nach normalen Fragen gehören dazu
        const prevQuestion = allQs[i - 1];
        if (prevQuestion && 
            prevQuestion.type !== 'admin_file' && 
            prevQuestion.type !== 'info_text') {
          // Gehört zur vorherigen Frage
          continue;
        }
      }
      
      // Alle anderen sind navigierbar
      navigable.push(question);
      insideSection = false;
    }
    
    return navigable;
  }, [questionnaireData?.template?.questions]);

  const allQuestions = useCallback(() => {
    if (!questionnaireData?.template?.questions) return [];
    return questionnaireData.template.questions.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  }, [questionnaireData?.template?.questions]);

  const currentQuestion = navigableQuestions()[currentQuestionIndex];

  // **FUNKTION UM AKTUELLE SECTION ZU FINDEN**
  const getCurrentSection = useCallback(() => {
    // **WICHTIG: Wenn die aktuelle Frage selbst eine Section ist, gib null zurück**
    // Die Section wird dann vom QuestionRenderer gerendert
    if (currentQuestion?.type === 'section') {
      return null;
    }
    
    if (!currentQuestion?.order) {
      return null;
    }
    
    // Finde alle sections
    const allSections = allQuestions().filter(q => q.type === 'section');
    
    if (allSections.length === 0) {
      return null;
    }
    
    // **VEREINFACHTE LOGIK: Finde die Section, die zu dieser Frage gehört**
    // Suche die letzte Section die <= der aktuellen Frage ist
    const relevantSections = allSections.filter(s => s.order && s.order <= (currentQuestion.order || 0));
    
    // **FALLBACK: Wenn keine Section <= Frage, nimm die erste Section überhaupt**
    if (relevantSections.length === 0) {
      if (currentQuestionIndex === 0 && allSections.length > 0) {
        // Bei der ersten Frage: zeige die erste Section
        allSections.sort((a, b) => (a.order || 0) - (b.order || 0));
        const firstSection = allSections[0];
        return firstSection;
      }
      return null;
    }
    
    // Sortiere nach order und nimm die letzte (höchste order)
    relevantSections.sort((a, b) => (b.order || 0) - (a.order || 0));
    const currentSection = relevantSections[0];
    
    // **EINFACHERE LOGIK: Zeige Section nur wenn sich die Section ändert**
    if (currentQuestionIndex === 0) {
      return currentSection;
    }
    
    // Prüfe die vorherige Frage
    const visibleQuestionsInOrder = navigableQuestions();
    const previousQuestion = visibleQuestionsInOrder[currentQuestionIndex - 1];
    
    if (previousQuestion) {
      const previousSections = allSections.filter(s => s.order && s.order <= (previousQuestion.order || 0));
      previousSections.sort((a, b) => (b.order || 0) - (a.order || 0));
      const previousSection = previousSections[0];
      
      if (!previousSection || previousSection.id !== currentSection.id) {
        return currentSection;
      } else {
        return null;
      }
    }
    
    return currentSection;
  }, [currentQuestion, allQuestions, currentQuestionIndex, visibleQuestions]);

  const calculateProgress = useCallback(() => {
    // Only count questions that require answers (not info_text, admin_file, or sections)
    const answerableQuestions = navigableQuestions().filter(q => 
      q.type !== 'info_text' && 
      q.type !== 'admin_file' && 
      q.type !== 'section'
    );
    const answeredQuestionsCount = answerableQuestions.filter(q => answers.has(q.id)).length;
    
    return answerableQuestions.length > 0 ? Math.round((answeredQuestionsCount / answerableQuestions.length) * 100) : 100;
  }, [navigableQuestions, answers]);

  // **NEW: Check if submission is allowed based on required fields**
  const canSubmitBasedOnRequiredFields = useCallback(() => {
    const requiredQuestions = navigableQuestions().filter(q => 
      q.required && 
      q.type !== 'section' && 
      q.type !== 'info_text' && 
      q.type !== 'admin_file'
    );
    
    // If no required questions, allow submission immediately
    if (requiredQuestions.length === 0) {
      return true;
    }
    
    // Check if all required questions are answered
    const unansweredRequired = requiredQuestions.filter(q => !answers.has(q.id));
    
    const allRequiredAnswered = unansweredRequired.length === 0;
    
    return allRequiredAnswered;
  }, [visibleQuestions, answers]);

  const progress = calculateProgress();
  
  // **UPDATED: Improved canSubmit logic**
  // Allow submission if:
  // 1. All required fields are filled OR
  // 2. Progress is >= 80% (fallback for edge cases)
  const canSubmit = canSubmitBasedOnRequiredFields() || progress >= 80;
  const currentVisibleQuestions = navigableQuestions();

  // **SESSION CHECK**
  const checkSession = useCallback(() => {
    if (isPortalMode && masterToken) {
      if (!portalSessionToken) {
        navigate(`/supplier/portal/${masterToken}`, { replace: true });
        return false;
      }
      
      const storedPortalToken = sessionStorage.getItem('supplierPortalSession');
      const sessionTime = sessionStorage.getItem('supplierPortalSessionTime');
      
      if (storedPortalToken !== portalSessionToken) {
        navigate(`/supplier/portal/${masterToken}`, { replace: true });
        return false;
      }
      
      if (!sessionTime || Date.now() - parseInt(sessionTime) > 45 * 60 * 1000) {
        navigate(`/supplier/portal/${masterToken}`, { replace: true });
        return false;
      }
      
      sessionStorage.setItem('supplierPortalSessionTime', Date.now().toString());
      return true;
    }
    
    // **ÜBERPRÜFE PORTAL-SESSION TOKENS AUCH OHNE PORTAL MODE**
    // Falls vom Portal weitergeleitet wurde
    const fromSupplierLogin = sessionStorage.getItem('fromSupplierLogin');
    const portalSession = sessionStorage.getItem('supplierPortalSession');
    
    if (fromSupplierLogin === 'true' && portalSession) {
      return true;
    }
    
    const storedToken = sessionStorage.getItem('supplierSessionToken');
    const expiry = sessionStorage.getItem('supplierSessionExpiry');
    
    if (!storedToken || !expiry || Date.now() >= parseInt(expiry)) {
      navigate(`/supplier/login/${accessToken}`, {
        replace: true,
        state: { sessionExpired: true }
      });
      return false;
    }
    return true;
  }, [accessToken, masterToken, isPortalMode, portalSessionToken, navigate]);

  // **DATEN LADEN**
  useEffect(() => {
    const loadData = async () => {
      if (!accessToken) {
        setError('Ungültiger Zugangs-Link');
        setLoading(false);
        return;
      }

      if (!checkSession()) return;

      try {
        setLoading(true);
        
        let data: SupplierQuestionnaireData;
        
        if (isPortalMode && masterToken && portalSessionToken) {
          data = await loadPortalQuestionnaire(masterToken, accessToken, portalSessionToken);
        } else {
          data = await loadQuestionnaire(accessToken);
        }
        
        setQuestionnaireData(data);
        
        const answersMap = new Map<string, Answer>();
        if (data.answers && data.answers.length > 0) {
          data.answers.forEach(answer => {
            answersMap.set(answer.questionId, answer);
          });
        }
        setAnswers(answersMap);
        
      } catch (error: any) {
        console.error('❌ Error loading questionnaire:', error);
        
        if (error.message.includes('Session') || 
            error.message.includes('Token') || 
            error.message.includes('Unauthorized')) {
          if (isPortalMode && masterToken) {
            navigate(`/supplier/portal/${masterToken}`, { replace: true });
          } else {
            navigate(`/supplier/login/${accessToken}`, { replace: true });
          }
        } else {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken, masterToken, isPortalMode, portalSessionToken, navigate, checkSession]);

  // **NAVIGATION FUNKTIONEN**
  const goToQuestion = useCallback((index: number) => {
    if (index >= 0 && index < currentVisibleQuestions.length) {
      setCurrentQuestionIndex(index);
    }
  }, [currentVisibleQuestions.length]);

  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex]);

  const goToNextQuestion = useCallback(() => {
    if (currentQuestionIndex < currentVisibleQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, currentVisibleQuestions.length]);

  // **ANTWORT SPEICHERN**
  const handleSaveAnswer = useCallback(async (questionId: string, answer: any, attachments?: File[]) => {
    if (!accessToken) return;

    // **SESSION PRÜFUNG BASIEREND AUF MODUS**
    let sessionToken: string | null = null;
    
    if (isPortalMode && masterToken) {
      if (!checkSession()) return;
      sessionToken = portalSessionToken;
    } else {
      // **PRÜFE OB VOM PORTAL ÜBERTRAGEN**
      const fromSupplierLogin = sessionStorage.getItem('fromSupplierLogin');
      const portalSession = sessionStorage.getItem('supplierPortalSession');
      
      if (fromSupplierLogin === 'true' && portalSession) {
        sessionToken = portalSession;
      } else {
        sessionToken = sessionStorage.getItem('supplierSessionToken');
        if (!sessionToken) {
          navigate(`/supplier/login/${accessToken}`, {
            replace: true,
            state: { sessionExpired: true }
          });
          return;
        }
      }
    }

    if (!sessionToken) {
      return;
    }

    try {
      setAutoSaving(true);
      
      let response: { progress: any; attachments?: string[] };
      
      // **VERWENDE PORTAL API FALLS VOM PORTAL ÜBERTRAGEN ODER PORTAL MODE**      
      if (shouldUsePortalAPI()) {
        const apiMasterToken = getPortalMasterToken();
        response = await savePortalAnswer(
          apiMasterToken!,
          accessToken,
          sessionToken,
          questionId,
          answer,
          attachments,
          language
        );
      } else {
        response = await saveAnswer(
          accessToken,
          sessionToken,
          questionId,
          answer,
          attachments
        );
      }

      setAnswers(prev => {
        const existingAnswer = prev.get(questionId);
        const updatedAnswer = {
          questionId,
          answer,
          attachments: response.attachments || existingAnswer?.attachments || [],
          answeredAt: new Date().toISOString()
        };
        
        return new Map(prev.set(questionId, updatedAnswer));
      });
      
    } catch (error: any) {
      console.error('❌ Error saving answer:', error);
      
      if (error.message.includes('Session') || error.message.includes('Unauthorized')) {
        if (isPortalMode && masterToken) {
          sessionStorage.removeItem('supplierPortalSession');
          sessionStorage.removeItem('supplierPortalSessionTime');
          navigate(`/supplier/portal/${masterToken}`, { replace: true });
        } else {
          sessionStorage.removeItem('supplierSessionToken');
          sessionStorage.removeItem('supplierSessionExpiry');
          navigate(`/supplier/login/${accessToken}`, {
            replace: true,
            state: { 
              sessionExpiredDuringSave: true,
              lastQuestion: questionId 
            }
          });
        }
      }
    } finally {
      setAutoSaving(false);
    }
  }, [accessToken, navigate, isPortalMode, masterToken, portalSessionToken, checkSession]);

  // **ARTIKEL AUSWAHL HANDLER**
  const handleArticleSelectionChange = useCallback((questionId: string, articleIds: string[]) => {
    setArticleSelections(prev => new Map(prev.set(questionId, articleIds)));
    
    // Speichere auch als normale Antwort für Persistierung
    handleSaveAnswer(questionId, { type: 'article_selection', selectedArticles: articleIds });
  }, [handleSaveAnswer]);

  // **HELPER: Hole verfügbare Artikel aus der Fragebogen-Konfiguration**
  const getAvailableArticles = useCallback(() => {
    // Prüfe ob das Template Artikel-Daten hat
    if (questionnaireData?.template?.articles && questionnaireData.template.articles.length > 0) {
      return questionnaireData.template.articles.map(article => ({
        _id: article._id,
        articleNumber: article.articleNumber,
        name: article.name,
        description: article.description || '',
        brand: (article as any).brand || '',
        gtin: (article as any).gtin || '',
        status: (article as any).status || 'draft',
        supplierId: article.supplierId || questionnaireData.supplier.name
      }));
    }
    
    return [];
  }, [questionnaireData]);

  // **FRAGEBOGEN ABSCHLIESSEN**
  const handleSubmitQuestionnaire = useCallback(async () => {
    if (!accessToken) return;

    // **SESSION PRÜFUNG BASIEREND AUF MODUS**
    let sessionToken: string | null = null;
    
    if (isPortalMode && masterToken) {
      if (!checkSession()) return;
      sessionToken = portalSessionToken;
    } else {
      // **PRÜFE OB VOM PORTAL ÜBERTRAGEN**
      const fromSupplierLogin = sessionStorage.getItem('fromSupplierLogin');
      const portalSession = sessionStorage.getItem('supplierPortalSession');
      
      if (fromSupplierLogin === 'true' && portalSession) {
        sessionToken = portalSession;
      } else {
        sessionToken = sessionStorage.getItem('supplierSessionToken');
        if (!sessionToken) {
          navigate(`/supplier/login/${accessToken}`, {
            replace: true,
            state: { sessionExpired: true }
          });
          return;
        }
      }
    }

    if (!sessionToken) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // **VERWENDE PORTAL API FALLS VOM PORTAL ÜBERTRAGEN ODER PORTAL MODE**      
      if (shouldUsePortalAPI()) {
        const apiMasterToken = getPortalMasterToken();
        await submitPortalQuestionnaire(
          apiMasterToken!,
          accessToken,
          sessionToken
        );
      } else {
        await submitQuestionnaire(
          accessToken,
          sessionToken
        );
      }

      // **DON'T REMOVE SESSION TOKENS - USER SHOULD STAY LOGGED IN**
      // Portal users should return to dashboard after submission
      // if (isPortalMode) {
      //   sessionStorage.removeItem('supplierPortalSession');
      //   sessionStorage.removeItem('supplierPortalSessionTime');
      // } else {
      //   sessionStorage.removeItem('supplierSessionToken');
      //   sessionStorage.removeItem('supplierSessionExpiry');
      //   sessionStorage.removeItem('fromSupplierLogin');
      // }
      
      // **NAVIGATION ZUR SUCCESS SEITE**      
      if (shouldUsePortalAPI()) {
        const apiMasterToken = getPortalMasterToken();
        navigate(`/supplier/portal/${apiMasterToken}/questionnaire/success?supplier=${encodeURIComponent(supplierName || '')}&questionnaire=${encodeURIComponent(getTemplateTitle())}`, { 
          replace: true 
        });
      } else {
        navigate(`/questionnaire/${accessToken}/success`, { 
          replace: true,
          state: { submissionSuccess: true }
        });
      }
      
    } catch (error: any) {
      console.error('❌ Error submitting questionnaire:', error);
      
      if (error.message.includes('Session') || error.message.includes('Unauthorized')) {
        if (isPortalMode && masterToken) {
          sessionStorage.removeItem('supplierPortalSession');
          sessionStorage.removeItem('supplierPortalSessionTime');
          navigate(`/supplier/portal/${masterToken}`, { replace: true });
        } else {
          sessionStorage.removeItem('supplierSessionToken');
          sessionStorage.removeItem('supplierSessionExpiry');
          navigate(`/supplier/login/${accessToken}`, {
            replace: true,
            state: { sessionExpiredDuringSubmit: true }
          });
        }
      }
    } finally {
      setIsSubmitting(false);
      setShowSubmissionModal(false);
    }
  }, [accessToken, navigate, isPortalMode, masterToken, portalSessionToken, checkSession, getTemplateTitle]);

  // **LOADING STATE**
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <img src={wedlLogo} alt="WEDL" className="w-12 h-12" />
          </div>
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.loadingQuestionnaire}</h2>
          <p className="text-gray-600">{t.loadingMessage}</p>
        </div>
      </div>
    );
  }

  // **ERROR STATE**
  if (error || !questionnaireData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-red-500 mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t.errorLoading}</h2>
          <p className="text-gray-600 mb-6">{error || t.errorMessage}</p>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                if (isPortalMode && masterToken) {
                  navigate(`/supplier/portal/${masterToken}`, { replace: true });
                } else {
                  navigate(`/supplier/login/${accessToken}`, { replace: true });
                }
              }}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center justify-center font-medium shadow-md hover:shadow-lg"
            >
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
              {t.toLogin}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 font-medium"
            >
              {t.reloadPage}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // **COMPLETED STATE**
  if (questionnaireData.submission.status === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
        {/* **HEADER** */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <img src={wedlLogo} alt="WEDL" className="h-8 w-auto" />
                <div className="h-8 w-px bg-gray-300"></div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {questionnaireData.supplier.name}
                </h1>
              </div>
              
              <button
                onClick={() => {
                  if (isPortalMode && masterToken) {
                    navigate(`/supplier/portal/${masterToken}`);
                  } else {
                    window.close();
                  }
                }}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FontAwesomeIcon icon={faHome} className="mr-2" />
                {isPortalMode ? 'Zum Portal' : 'Schließen'}
              </button>
            </div>
          </div>
        </div>

        {/* **COMPLETED CONTENT** */}
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md mx-auto bg-white rounded-xl shadow-lg p-8"
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{t.thankYou}</h2>
            <p className="text-gray-600 mb-6 text-lg">
              {t.questionnaireCompleted}
            </p>
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>{t.supplier}:</strong> {questionnaireData.supplier.name}</p>
              <p><strong>{t.questionnaire}:</strong> {getTemplateTitle()}</p>
              <p><strong>{t.completedOn}:</strong> {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE')}</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* **WEDL GRAUER HEADER** */}
      <header className="bg-gray-700 shadow-md sticky top-0 z-40">
        {/* **ENTFERNE max-w-7xl mx-auto - Das limitiert die Breite!** */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 w-full">
            {/* **LEFT: LOGO & BRAND INFO - GANZ LINKS** */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              <img src={wedlLogo} alt="WEDL" className="h-10 w-auto" />
              <div className="h-10 w-px bg-gray-500"></div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white truncate">{getTemplateTitle()}</h1>
                <p className="text-sm text-gray-300 truncate">
                  {questionnaireData.supplier.name}
                  {isReadonly && <span className="ml-2 text-amber-400">• {t.viewOnly}</span>}
                </p>
              </div>
            </div>

            {/* **RIGHT: PORTAL BUTTON - GANZ RECHTS** */}
            <div className="flex items-center flex-shrink-0 ml-auto">
              {isPortalMode && (
                <button
                  onClick={() => navigate(`/supplier/portal/${masterToken}`)}
                  className="flex items-center px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded-lg transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faHome} className="mr-2" />
                  {t.portal}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* **MAIN CONTENT** */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* **NAVIGATION CONTROLS ÜBER DER FRAGE** */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            {/* **LEFT: ZURÜCK BUTTON - WEDL ROT** */}
            <button
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className={`flex items-center space-x-1 px-3 py-2 text-sm rounded-md font-medium transition-all duration-200 ${
                currentQuestionIndex === 0
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : 'text-white bg-red-600 hover:bg-red-700 shadow-sm'
              }`}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
              <span>{t.back}</span>
            </button>

            {/* **CENTER: QUESTION DOTS NAVIGATION** */}
            <div className="flex items-center space-x-1 max-w-md overflow-x-auto">
              {currentVisibleQuestions.slice(0, 15).map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => goToQuestion(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 flex-shrink-0 ${
                    index === currentQuestionIndex
                      ? 'bg-red-600 ring-2 ring-red-200 w-4 h-4'
                      : answers.has(q.id)
                      ? 'bg-gray-600 hover:bg-gray-700'  // **CHANGED: Von grün zu grau**
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={`${t.question} ${index + 1}${answers.has(q.id) ? ` (${t.answered})` : ''}`}
                />
              ))}
              {currentVisibleQuestions.length > 15 && (
                <span className="text-gray-400 ml-2 text-xs">...</span>
              )}
            </div>

            {/* **RIGHT: WEITER/SUBMIT BUTTON - WEDL ROT** */}
            {currentQuestionIndex === currentVisibleQuestions.length - 1 ? (
              <button
                onClick={() => setShowSubmissionModal(true)}
                disabled={!canSubmit || !questionnaireData.canEdit || isReadonly}
                className={`flex items-center space-x-1 px-3 py-2 text-sm rounded-md font-medium transition-all duration-200 ${
                  canSubmit && questionnaireData.canEdit && !isReadonly
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                <span>{isReadonly ? t.viewOnly : t.submit}</span>
              </button>
            ) : (
              <button
                onClick={goToNextQuestion}
                className="flex items-center space-x-1 px-3 py-2 text-sm bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-all duration-200 shadow-md"
              >
                <span>{t.next}</span>
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </button>
            )}
          </div>

          {/* **PROGRESS INFO** */}
          <div className="text-center mb-4">
            <span className="text-lg font-bold text-gray-800">
              {(() => {
                // **Zähle navigierbare Fragen**
                const navigableQs = navigableQuestions();
                const totalNavigable = navigableQs.length;
                const currentNavigableIndex = currentQuestionIndex + 1; // 1-basiert
                
                // Sections sind auch Fragen!
                if (totalNavigable > 0) {
                  return `${t.question} ${currentNavigableIndex} ${t.of} ${totalNavigable}`;
                } else {
                  return language === 'en' ? 'Information' : 'Informationen';
                }
              })()}
            </span>
            <div className="flex items-center justify-center space-x-4 mt-2 text-sm text-gray-600">
              {(() => {
                // Zähle alle beantwortbaren Fragen (inkl. Section-Unterfragen)
                const allAnswerableQuestions: string[] = [];
                currentVisibleQuestions.forEach(q => {
                  if (q.type === 'section' && q.questions) {
                    // Füge alle Unterfragen der Section hinzu
                    q.questions.forEach((subQ: Question) => {
                      if (subQ.type !== 'info_text' && subQ.type !== 'admin_file') {
                        allAnswerableQuestions.push(subQ.id);
                      }
                    });
                  } else if (q.type !== 'info_text' && q.type !== 'admin_file') {
                    allAnswerableQuestions.push(q.id);
                  }
                });
                
                const answeredCount = allAnswerableQuestions.filter(qId => answers.has(qId)).length;
                
                if (allAnswerableQuestions.length === 0) {
                  return <span>{language === 'en' ? 'Information only - no questions to answer' : 'Nur Informationen - keine Fragen zu beantworten'}</span>;
                } else {
                  return (
                    <>
                      <span>{answeredCount} {t.answered}</span>
                      <span>•</span>
                      <span>{progress}% {t.completed}</span>
                    </>
                  );
                }
              })()}
              {autoSaving && (
                <>
                  <span>•</span>
                  <span className="flex items-center space-x-1 text-blue-600">
                    <FontAwesomeIcon icon={faSave} className="animate-spin" />
                    <span>{language === 'en' ? 'Saving...' : 'Speichert...'}</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* **QUESTION CONTENT** */}
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <motion.div
              key={`question-${currentQuestion.id}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
            >
              <div className="p-8">
                {/* **SECTION HEADER (FALLS VORHANDEN)** */}
                {(() => {
                  const section = getCurrentSection();
                  
                  if (section) {
                    const sectionTitle = section.title || section.label;
                    const displayTitle = typeof sectionTitle === 'string' ? sectionTitle : sectionTitle?.de || sectionTitle?.en || 'Abschnitt';
                    
                    return (
                      <div className="mb-8">
                        <div className="my-8 first:mt-0">
                          <h2 className="text-xl font-bold text-gray-900 border-b-2 border-blue-500 pb-3 mb-6">
                            {displayTitle}
                          </h2>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* **AKTUELLE FRAGE** */}
                <QuestionRenderer
                  question={currentQuestion}
                  answer={answers.get(currentQuestion.id)}
                  onSave={(questionId: string, answer: any, attachments?: File[]) => handleSaveAnswer(questionId, answer, attachments)}
                  language={language}
                  readOnly={!questionnaireData.canEdit || isReadonly}
                  supplierName={questionnaireData.supplier.name}
                  supplierId={(questionnaireData.supplier as any)._id}
                  allQuestions={allQuestions()}
                  currentIndex={allQuestions().findIndex(q => q.id === currentQuestion.id)}
                  allAnswers={answers}
                  // **NEU: Artikel-Daten**
                  availableArticles={getAvailableArticles()}
                  selectedArticleAnswer={articleSelections.get(currentQuestion.id) || []}
                  onArticleSelectionChange={(articleIds: string[]) => handleArticleSelectionChange(currentQuestion.id, articleIds)}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* **SUBMISSION MODAL** */}
      <SubmissionModal
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        onSubmit={handleSubmitQuestionnaire}
        supplierName={questionnaireData.supplier.name}
        templateTitle={getTemplateTitle()}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default SupplierQuestionnaire;